'use client';

import { useState, type ReactNode } from 'react';
import { Handle, Position } from '@xyflow/react';
import { NODE_SPECS, SOCKET_COLORS } from '@/lib/pipeline/registry';
import { usePipelineStore } from '@/lib/pipeline/store';
import { unconfiguredReason } from '@/lib/pipeline/validate';
import { NodeStatus } from '@/lib/pipeline/types';

const STATUS_RING: Record<NodeStatus, string> = {
  idle: 'ring-1 ring-[var(--border-subtle)]',
  running: 'ring-2 ring-[var(--accent)] animate-pulse',
  done: 'ring-1 ring-[var(--success)]',
  error: 'ring-2 ring-[var(--error)]',
  cached: 'ring-1 ring-[var(--info)]',
};

const STATUS_DOT: Record<NodeStatus, string> = {
  idle: 'bg-[var(--text-muted)]',
  running: 'bg-[var(--accent)] animate-pulse',
  done: 'bg-[var(--success)]',
  error: 'bg-[var(--error)]',
  cached: 'bg-[var(--info)]',
};

/** Shared node chrome: header, status ring, evenly-spaced typed handles, error footer. */
export function NodeShell({
  id,
  type,
  children,
}: {
  id: string;
  type: string;
  children?: ReactNode;
}) {
  const spec = NODE_SPECS[type];
  const rt = usePipelineStore((s) => s.runtime[id]);
  const node = usePipelineStore((s) => s.nodes.find((n) => n.id === id));
  const edges = usePipelineStore((s) => s.edges);
  const removeNode = usePipelineStore((s) => s.removeNode);
  const update = usePipelineStore((s) => s.updateNodeData);
  const [editing, setEditing] = useState(false);
  const title = (node?.data as { title?: string })?.title;
  const status: NodeStatus = rt?.status ?? 'idle';

  const warning = node ? unconfiguredReason(node, edges) : null;
  const showWarning = !!warning && status === 'idle' && !rt?.error;
  const ring =
    status === 'error'
      ? STATUS_RING.error
      : showWarning
        ? 'ring-2 ring-[var(--warning)]'
        : STATUS_RING[status];

  const inputs = spec?.inputs ?? [];
  const outputs = spec?.outputs ?? [];

  return (
    <div
      className={`rounded-xl bg-[var(--bg-surface)] border border-[var(--border-subtle)] shadow-soft w-[240px] ${ring}`}
    >
      <div className="px-3 py-2 border-b border-[var(--border-subtle)] flex items-center justify-between gap-2">
        {editing ? (
          <input
            autoFocus
            defaultValue={title ?? ''}
            placeholder={spec?.label ?? type}
            className="nodrag input h-6 py-0 px-1 text-sm font-semibold w-full"
            onClick={(e) => e.stopPropagation()}
            onBlur={(e) => {
              update(id, { title: e.target.value.trim() || null });
              setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                update(id, { title: (e.target as HTMLInputElement).value.trim() || null });
                setEditing(false);
              } else if (e.key === 'Escape') {
                setEditing(false);
              }
            }}
          />
        ) : (
          <span
            className="text-sm font-semibold truncate cursor-text"
            title="Double-click to rename"
            onDoubleClick={() => setEditing(true)}
          >
            {title || spec?.label || type}
            {title && (
              <span className="text-[10px] font-normal text-[var(--text-muted)] ml-1">
                {spec?.label}
              </span>
            )}
          </span>
        )}
        <div className="flex items-center gap-2 shrink-0">
          <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status]}`} />
          <button
            onClick={() => removeNode(id)}
            className="nodrag text-[var(--text-muted)] hover:text-[var(--error)] leading-none text-sm"
            title="Delete node"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Labeled sockets: inputs left, outputs right, paired into rows so
          output handles sit at the top of the node (easy to grab) rather
          than below a tall body. */}
      {(inputs.length > 0 || outputs.length > 0) && (
        <div className="py-1.5">
          {Array.from({ length: Math.max(inputs.length, outputs.length) }).map((_, i) => {
            const inp = inputs[i];
            const out = outputs[i];
            return (
              <div
                key={i}
                className="relative flex items-center justify-between gap-2 h-[19px]"
              >
                {inp ? (
                  <span className="flex items-center min-w-0">
                    <Handle
                      id={inp.id}
                      type="target"
                      position={Position.Left}
                      title={`${inp.label} (${inp.type})`}
                      style={{
                        width: 10,
                        height: 10,
                        background: SOCKET_COLORS[inp.type],
                        border: '2px solid var(--bg-surface)',
                      }}
                    />
                    <span className="text-[10px] text-[var(--text-muted)] pl-3 truncate">
                      {inp.label}
                    </span>
                  </span>
                ) : (
                  <span />
                )}
                {out ? (
                  <span className="flex items-center min-w-0">
                    <span className="text-[10px] text-[var(--text-muted)] pr-3 truncate">
                      {out.label}
                    </span>
                    <Handle
                      id={out.id}
                      type="source"
                      position={Position.Right}
                      title={`${out.label} (${out.type})`}
                      style={{
                        width: 10,
                        height: 10,
                        background: SOCKET_COLORS[out.type],
                        border: '2px solid var(--bg-surface)',
                      }}
                    />
                  </span>
                ) : (
                  <span />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="px-3 py-2 space-y-2">{children}</div>

      {rt?.error && (
        <div className="px-3 pb-2 text-[11px] text-[var(--error)] break-words">{rt.error}</div>
      )}
      {showWarning && (
        <div className="px-3 pb-2 text-[11px] text-[var(--warning)] break-words">⚠ {warning}</div>
      )}
    </div>
  );
}
